import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Coins, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getPendingReferralCode } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CREDIT_PACKS = [
  { id: 'credits_100', credits: 100, amount: 20 },
  { id: 'credits_250', credits: 250, amount: 50 },
  { id: 'credits_500', credits: 500, amount: 100 },
  { id: 'credits_1250', credits: 1250, amount: 250 },
  { id: 'credits_3000', credits: 3000, amount: 500, bonus: true, originalCredits: 2500 },
  { id: 'credits_6000', credits: 6000, amount: 1000, bonus: true, originalCredits: 5000 },
];

const formatCredits = (n) => n.toLocaleString('en-US');

export default function BuyCreditsModal({ open, onOpenChange }) {
  const [loadingId, setLoadingId] = useState(null);

  const startCheckout = async (packId) => {
    setLoadingId(packId);
    try {
      const userKey = localStorage.getItem('tfu_session') || (() => {
        const k = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('tfu_session', k);
        return k;
      })();
      const referralCode = getPendingReferralCode();
      const res = await axios.post(
        `${API}/checkout/session`,
        {
          package_id: packId,
          origin_url: window.location.origin,
          user_key: userKey,
          referral_code: referralCode || undefined,
        },
        { withCredentials: true }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Could not start checkout. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Checkout failed');
      setLoadingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="buy-credits-modal"
        className="max-w-3xl p-0 overflow-hidden bg-neutral-950 border border-neutral-800 text-white [&>button]:bg-neutral-800 [&>button]:hover:bg-neutral-700 [&>button]:rounded-full [&>button]:w-8 [&>button]:h-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:text-neutral-400 [&>button]:opacity-100 [&>button]:right-5 [&>button]:top-5"
      >
        <div className="px-6 pt-6 pb-4">
          <DialogHeader className="space-y-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-300 to-orange-500 flex items-center justify-center">
                <Coins className="w-5 h-5 text-amber-950" />
              </div>
              <DialogTitle className="font-display text-2xl text-white">Purchase Credits</DialogTitle>
            </div>
            <DialogDescription className="sr-only">
              Choose a one-time credit pack to top up your account.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Referral banner */}
        <div className="mx-6 mb-5 rounded-2xl bg-gradient-to-r from-emerald-200/90 to-teal-200/90 px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/40 flex items-center justify-center text-xl">
            🎁
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-emerald-950 leading-tight">
              Refer a friend to earn $200 worth of credits
            </p>
            <p className="text-[12px] text-emerald-900/80 leading-tight mt-0.5">
              Invite friends through your referral link and unlock free credits.
            </p>
          </div>
          <a
            href="/referrals"
            data-testid="referral-know-more-btn"
            className="shrink-0 px-3.5 py-2 rounded-full bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800 transition"
          >
            Know more
          </a>
        </div>

        {/* Credit pack grid */}
        <div className="px-6 pb-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {CREDIT_PACKS.map((pack) => {
            const isLoading = loadingId === pack.id;
            return (
              <div
                key={pack.id}
                data-testid={`credit-pack-${pack.id}`}
                className={`relative rounded-2xl p-4 flex flex-col ${
                  pack.bonus
                    ? 'bg-gradient-to-b from-amber-950/40 to-neutral-900 border border-amber-700/40'
                    : 'bg-neutral-900 border border-neutral-800'
                }`}
              >
                {pack.bonus && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold tracking-wider whitespace-nowrap">
                    20% More
                  </span>
                )}

                <div className="flex-1 flex flex-col items-center justify-center text-center py-3 min-h-[110px]">
                  {pack.bonus && (
                    <div className="text-neutral-500 line-through text-sm mb-0.5">
                      {formatCredits(pack.originalCredits)} credits
                    </div>
                  )}
                  <div
                    className={`font-display font-bold text-2xl ${
                      pack.bonus ? 'text-amber-300' : 'text-white'
                    }`}
                  >
                    {formatCredits(pack.credits)} credits
                  </div>
                  <div className="mt-2 text-emerald-400 font-semibold text-lg">
                    ${formatCredits(pack.amount)}
                  </div>
                </div>

                <Button
                  onClick={() => startCheckout(pack.id)}
                  disabled={!!loadingId}
                  data-testid={`buy-now-${pack.id}`}
                  className="w-full h-10 rounded-xl bg-white hover:bg-neutral-100 text-neutral-900 font-semibold disabled:opacity-60"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…
                    </>
                  ) : (
                    'Buy Now'
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="px-6 pb-5">
          <p className="text-[11px] text-neutral-500 text-center">
            Secure payment via Stripe · Credits never expire · Visa, Mastercard, Amex, Apple Pay
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
